import React, { useState } from 'react'
import moment, { Moment } from 'moment/moment'
import clsx from 'clsx'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import { getCalendar } from '../../api/common'
import { setCheckIn, setCheckOut, setDatesCalendar, updateDataCalendar } from '../../redux/slices/property'
import { setLoading } from '../../redux/slices/common'
import { DATE_FORMAT, VIEW } from '../../constant'
import BasicButton from '../ui/BasicButton'
import BasicCalendar from '../common/BasicCalendar'
import { InView } from 'react-intersection-observer'

interface Props {
  hostId: string
  listingId?: string
  updateView: (_v: string) => void
  propertyCurrency?: string
}

const SelectDates: React.FC<Props> = ({ hostId, listingId, updateView, propertyCurrency }) => {
  const dispatch = useAppDispatch()
  const [months, setMonths] = useState<Moment[]>([moment().startOf('month'), moment().add(1, 'month').startOf('month')])
  const { datesCalendar, dataCalendar } = useAppSelector((state) => state.property)
  const { startDate, endDate } = datesCalendar

  async function fetchCalendar(listingId: string, fromDate: string, toDate: string) {
    try {
      dispatch(setLoading(true))
      const res = await getCalendar(hostId, listingId, fromDate, toDate)
      if (Array.isArray(res.data)) {
        let _dataCalendar: any = {}
        for (let item of res.data) {
          _dataCalendar[item.date] = item
        }
        dispatch(updateDataCalendar(_dataCalendar))
      }
    } finally {
      dispatch(setLoading(false))
    }
  }

  async function loadMoreMonths() {
    const lastMonth = months[months.length - 1]
    const diff = lastMonth.diff(moment(months[0]), 'days')
    if (diff > 730) {
      return
    }
    const newMonth = moment(lastMonth).add(1, 'months')
    // await handleNextAndPrevCalendar('next', newMonth)
    setMonths([...months, newMonth])
    handleNextAndPrevCalendar('next', newMonth)
  }

  async function handleNextAndPrevCalendar(type: 'next' | 'prev', newMonth: Moment) {
    if (!listingId) return
    const from: string = moment(newMonth).startOf('months').format(DATE_FORMAT)
    const nextOrPrevMonth = moment(newMonth).add(type === 'next' ? 1 : -1, 'months')
    const to: string = moment(nextOrPrevMonth).endOf('months').format(DATE_FORMAT)
    await fetchCalendar(listingId, from, to)
  }

  const onDatesChange = (_startDate: Moment | null, _endDate: Moment | null) => {
    dispatch(
      setDatesCalendar({
        startDate: _startDate,
        endDate: _endDate,
      })
    )
  }

  const onClearDates = () => {
    dispatch(setCheckIn(null))
    dispatch(setCheckOut(null))
    dispatch(
      setDatesCalendar({
        startDate: null,
        endDate: null,
      })
    )
  }

  const onApply = () => {
    dispatch(setCheckIn(startDate ? moment(startDate).format(DATE_FORMAT) : null))
    dispatch(setCheckOut(endDate ? moment(endDate).format(DATE_FORMAT) : null))
    updateView(VIEW.DETAIL)
  }

  if (hostId && listingId) {
    return (
      <div className={'dtravel-w-full h-full'}>
        <div className={'mb-[24px]'}>
          <BasicCalendar
            months={months}
            startDate={startDate}
            endDate={endDate}
            dataCalendar={dataCalendar}
            onNextAndPrev={handleNextAndPrevCalendar}
            onDatesChange={onDatesChange}
            currency={propertyCurrency}
            propertyCurrency={propertyCurrency}
          />
          <InView
            id={`scroll_drag_btn`}
            as="div"
            onChange={(inView) => {
              if (inView) {
                loadMoreMonths()
              }
            }}
            rootMargin={'0px 0px 76px 0px'}
          >
            <div className={'h-[1px]'} />
          </InView>
        </div>

        {/*---Footer---*/}
        <div
          className={clsx(
            'sticky bottom-[-24px] left-0 !w-[calc(100%+48px)] h-[72px] shadow-xl bg-white',
            'px-[24px] py-[16px] ml-[-24px] mb-[-24px]',
            'flex dtravel-gap-8 justify-end items-center'
          )}
          style={{ zIndex: 1200 }}
        >
          <p className={'font-inter-400 text-12-16 text-neutral-600 flex-1'}>Prices in {propertyCurrency}</p>
          <BasicButton
            variant={'outlined'}
            color={'black'}
            clases={'w-auto'}
            size={'lg'}
            onClick={onClearDates}
            disabled={!startDate && !endDate}
          >
            Clear dates
          </BasicButton>
          <BasicButton
            variant={'contained'}
            color={'black'}
            clases={'w-auto'}
            size={'lg'}
            onClick={onApply}
            disabled={!startDate || !endDate}
          >
            Apply
          </BasicButton>
        </div>
      </div>
    )
  }
  return null
}

export default SelectDates
